'use client';

import { useId, type CSSProperties } from 'react';
import { BlockRoot } from '@akademi/site-builder-runtime';
import { Icon, isIconName, type IconName } from '../shared/Icon';
import { childLevel, headingLevel, items, SkLink, text, type HeadingLevel, type Localized } from '../shared/primitives';
import { SkBaseStyles, SkStyles } from '../styles/SkStyles';
import { ACCOUNT_TYPES_CSS } from '../trade/tradeCss';
import { anchorId, FITTER_APPLICATION_HREF } from '../trade/tradeShared';
import { homeSurfaceClass, HomeSurfaceStyles, type HomeSurface } from './homeShared';

/**
 * The account choice on the trade page (site mockup `trade.html`, "Choose your
 * account"): a card each for a trade account, a fitter account and both.
 *
 * <p>Every benefit on a card (discounts, credit terms, alerts, "most popular")
 * is a statement about the business, so it is the owner's to write: the block
 * ships titles and buttons only, and shows a bullet list, a description or a
 * flag only when one is authored.
 *
 * <p>The trade and "both" buttons default to `/trade`, the page that holds the
 * trade account application (commerce's `TradeApplicationForm`); the fitter
 * button defaults to `/fitter-application` (commerce's `InstallerApplicationForm`).
 * A card's own `ctaHref` wins over both, and `defaultHref` replaces `/trade`.
 */
export type AccountKind = 'trade' | 'fitter' | 'both';

export interface SpatialKitchenAccountCard {
  kind?: AccountKind;
  icon?: string;
  title?: Localized;
  description?: Localized;
  bullets?: Localized[];
  ctaLabel?: Localized;
  ctaHref?: string;
  /** Drawn with a green border and a raised shadow. */
  featured?: boolean;
  /** A small label on the card's top edge, e.g. the owner's "Most popular". Empty for none. */
  flag?: Localized;
}

export interface SpatialKitchenAccountTypesProps {
  eyebrow?: Localized;
  title?: Localized;
  intro?: Localized;
  cards?: SpatialKitchenAccountCard[];
  /** Where a trade or "both" card's button goes when it has no link of its own. A fitter card goes to `/fitter-application`. */
  defaultHref?: string;
  surface?: HomeSurface;
  headingLevel?: HeadingLevel;
  /** The section's id, for links such as `#accounts`. */
  anchorId?: string;
}

const KIND_DEFAULTS: Record<AccountKind, { title: string; cta: string; icon: IconName }> = {
  trade: { title: 'Trade account', cta: 'Open a trade account', icon: 'tag' },
  both: { title: 'Trade + Fitter', cta: 'Apply for both', icon: 'layers' },
  fitter: { title: 'Fitter account', cta: 'Become a fitter', icon: 'tools' },
};

const DEFAULT_CARDS: SpatialKitchenAccountCard[] = [{ kind: 'trade' }, { kind: 'both' }, { kind: 'fitter' }];

function kindOf(value: unknown): AccountKind {
  return value === 'fitter' || value === 'both' ? value : 'trade';
}

export function SpatialKitchenAccountTypes(props: SpatialKitchenAccountTypesProps) {
  const titleId = useId();
  const level = headingLevel(props.headingLevel, 'h2');
  const Title = level;
  const CardTitle = childLevel(level);
  const authored = items<SpatialKitchenAccountCard>(props.cards).slice(0, 3);
  const cards = authored.length ? authored : DEFAULT_CARDS;
  const defaultHref = props.defaultHref?.trim() || '/trade';
  const eyebrow = text(props.eyebrow, 'Choose your account');
  const intro = text(props.intro);

  return (
    <BlockRoot
      as="section"
      id={anchorId(props.anchorId)}
      className={`sk sk-acct sk-section ${homeSurfaceClass(props.surface, 'white')}`}
      aria-labelledby={titleId}
    >
      <SkBaseStyles />
      <HomeSurfaceStyles />
      <SkStyles id="account-types" css={ACCOUNT_TYPES_CSS} />
      <div className="sk-container">
        <div className="sk-section-head">
          {eyebrow && <p className="sk-eyebrow">{eyebrow}</p>}
          <Title id={titleId} className="sk-heading sk-h2">
            {text(props.title, 'Trade, fitter, or both')}
          </Title>
          {intro && <p className="sk-lead">{intro}</p>}
        </div>
        <ul className="sk-acct__grid sk-list-reset" style={{ '--sk-acct-cols': String(cards.length) } as CSSProperties}>
          {cards.map((card, index) => {
            const kind = kindOf(card.kind);
            const defaults = KIND_DEFAULTS[kind];
            const icon = isIconName(card.icon) ? card.icon : defaults.icon;
            const bullets = (Array.isArray(card.bullets) ? card.bullets : []).map((b) => text(b)).filter(Boolean);
            const description = text(card.description);
            const flag = text(card.flag);
            return (
              <li key={index} className={card.featured ? 'sk-acct__card sk-acct__card--featured' : 'sk-acct__card'}>
                {flag && <span className="sk-acct__flag">{flag}</span>}
                <span className="sk-acct__ico" aria-hidden="true">
                  <Icon name={icon} size={26} />
                </span>
                <CardTitle>{text(card.title, defaults.title)}</CardTitle>
                {description && <p className="sk-acct__desc">{description}</p>}
                {bullets.length > 0 ? (
                  <ul className="sk-acct__list">
                    {bullets.map((bullet, i) => (
                      <li key={i}>{bullet}</li>
                    ))}
                  </ul>
                ) : (
                  <span className="sk-acct__spacer" />
                )}
                <SkLink
                  href={card.ctaHref?.trim() || (kind === 'fitter' ? FITTER_APPLICATION_HREF : defaultHref)}
                  className={`sk-btn ${card.featured ? 'sk-btn--primary' : 'sk-btn--secondary'}`}
                >
                  {text(card.ctaLabel, defaults.cta)}
                </SkLink>
              </li>
            );
          })}
        </ul>
      </div>
    </BlockRoot>
  );
}
